import { UserPermissions } from '@order-management/roles';
import {
  Criteria,
  DeferredResponse,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SortDirection,
  TemporaryAccessDashboardTableRow,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Icon } from 'react-bulma-components';
import { FaPlus } from 'react-icons/fa6';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from 'react-router-dom';
import BackButton from '../../shared/back-button/back-button';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import useHttpRequest from '../../shared/hooks/http-request';
import useToast from '../../shared/hooks/toasts';
import useUpdateUrl from '../../shared/hooks/url';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { createSearchParams } from '../../shared/url';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import {
  fetchZspaceInternalUsersTemporaryCustomerPermissions,
  revokeTemporaryAccess,
} from '../admin-service';
import AdminTemporaryAccessManagementTable from '../admin-temporary-access-management-table/admin-temporary-access-management-table';
import { SalesOrdersAccessStorageService } from '../sales-orders-access-storage-service';

const EMPTY_TABLE_TEXT =
  'There are no users with temporary access at the moment';
const REVOKE_TEMPORARY_ACCESS_SUCCESS_MESSAGE =
  'Temporary access has been successfully revoked';
const REVOKE_TEMPORARY_ACCESS_ERROR_MESSAGE =
  'The temporary access could not be revoked. Please try again';

const temporaryAccessSalesOrdersSessionStorageService =
  new SalesOrdersAccessStorageService();

const initialCriteria = (searchParams?: URLSearchParams) => {
  return {
    itemsPerPage: parseInt(searchParams?.get('itemsPerPage') ?? '10'),
    pageNumber: parseInt(searchParams?.get('pageNumber') ?? '1'),
    search: searchParams?.get('search') ?? '',
    sortBy: searchParams?.get('sortBy') ?? 'so.number',
    sortDirection:
      (searchParams?.get('sortDirection') as SortDirection) ??
      SortDirection.DES,
  };
};

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = createSearchParams(request);
  const criteria = initialCriteria(searchParams);

  const response =
    fetchZspaceInternalUsersTemporaryCustomerPermissions(criteria);

  return defer({ response });
};

export function AdminTemporaryAccessManagementPageContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const response =
    useAsyncValue() as PaginatedAPIResponse<TemporaryAccessDashboardTableRow>;
  const updateUrl = useUpdateUrl();
  const revalidator = useRevalidator();
  const toast = useToast();
  const { executeHttpRequest, isLoading } = useHttpRequest();
  const [criteria, setCriteria] = useState<Criteria>(
    initialCriteria(searchParams)
  );

  const tableData = useMemo(() => response.data, [response.data]);

  const tableConfig: PaginatedContentConfig = useMemo(() => {
    return {
      ...criteria,
      pages: response.pages,
    };
  }, [criteria, response.pages]);

  const onGrantTemporaryAccessClick = useCallback(() => {
    temporaryAccessSalesOrdersSessionStorageService.remove();
    navigate('/admin/temporary-access/setup/sales-orders');
  }, [navigate]);

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<TemporaryAccessDashboardTableRow>) => {
      const newCriteria = {
        ...criteria,
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };

      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, updateUrl]
  );

  const handleRevokeTemporaryAccess = useCallback(
    async (id: string) =>
      executeHttpRequest({
        asyncFunction: async () => {
          await revokeTemporaryAccess(id);
          revalidator.revalidate();
          toast.success(REVOKE_TEMPORARY_ACCESS_SUCCESS_MESSAGE);
        },
        customErrorMessage: REVOKE_TEMPORARY_ACCESS_ERROR_MESSAGE,
      }),
    [executeHttpRequest, revalidator, toast]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4"
      header={
        <>
          <BackButton />
          <Columns marginless>
            <Columns.Column className="pl-4">
              <h1 className="is-size-3 has-text-weight-light pb-3">
                Temporary access management
              </h1>
              <h2 className="is-size-5 has-text-weight-light">
                Manage which users currently have temporary access to customer’s
                Sales Orders
              </h2>
            </Columns.Column>

            <Columns.Column display="flex" justifyContent="flex-end">
              <Columns>
                <If condition={tableData.length > 0}>
                  <Columns.Column>
                    <Button
                      color="primary-dark"
                      onClick={onGrantTemporaryAccessClick}
                    >
                      <Icon>
                        <FaPlus />
                      </Icon>
                      <span>Grant temporary access</span>
                    </Button>
                  </Columns.Column>
                </If>
              </Columns>
            </Columns.Column>
          </Columns>
        </>
      }
    >
      <AdminTemporaryAccessManagementTable
        dataSource={tableData}
        config={tableConfig}
        onChange={handleOnTableChange}
        handleRevokeTemporaryAccess={handleRevokeTemporaryAccess}
        loading={isLoading}
        empty={
          <Columns display="flex" flexDirection="column" alignItems="center">
            <Columns.Column>
              <span className="has-text-weight-light">{EMPTY_TABLE_TEXT}</span>
            </Columns.Column>
            <Columns.Column>
              <Button
                color="primary-dark"
                onClick={onGrantTemporaryAccessClick}
              >
                <Icon>
                  <FaPlus />
                </Icon>
                <span>Grant temporary access</span>
              </Button>
            </Columns.Column>
          </Columns>
        }
      />
    </BoxLayout>
  );
}

export function AdminTemporaryAccessManagementPage() {
  const { response } = useLoaderData() as DeferredResponse<
    Promise<PaginatedAPIResponse<TemporaryAccessDashboardTableRow>>
  >;

  return (
    <ProtectedPage permissions={UserPermissions.USERS_SELF_ASSIGN_ROLES}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <AdminTemporaryAccessManagementPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default AdminTemporaryAccessManagementPage;
